import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { api, qs, type CardSummary, type Page } from '../lib/api';
import { navigate } from '../lib/router';
import { Snippet, typeLabel, useEscape } from './ui';

/** ⌘K / "/" command palette with instant FTS results. */
export function SearchPalette({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<CardSummary[]>([]);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEscape(onClose);
  useLayoutEffect(() => input.current?.focus(), []);

  useEffect(() => {
    if (!q.trim()) {
      api.get<Page<CardSummary>>(`/cards${qs({ limit: 8 })}`).then((r) => setItems(r.items), () => {});
      return;
    }
    const t = setTimeout(() => {
      api.get<Page<CardSummary>>(`/cards${qs({ q, limit: 10 })}`).then((r) => {
        setItems(r.items);
        setSel(0);
      }, () => {});
    }, 120);
    return () => clearTimeout(t);
  }, [q]);

  const go = (c: CardSummary) => {
    onClose();
    navigate(`/c/${c.id}/${c.slug}`);
  };

  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="palette" role="dialog" aria-modal="true" aria-label="Search">
        <input
          class="dr-input"
          placeholder="Search recipes, ingredients, notes…"
          aria-label="Search"
          value={q}
          ref={input}
          onInput={(e) => setQ(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((s) => Math.min(s + 1, items.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((s) => Math.max(s - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              if (items[sel]) go(items[sel]);
              else if (q) {
                onClose();
                navigate(`/search?q=${encodeURIComponent(q)}`);
              }
            }
          }}
        />
        <ul role="listbox" aria-label="Results">
          {items.map((c, i) => (
            <li key={c.id}>
              <a
                href="#"
                role="option"
                aria-selected={i === sel}
                onClick={(e) => {
                  e.preventDefault();
                  go(c);
                }}
              >
                <div>
                  <span class="dr-plate">{typeLabel(c.type)}</span>{' '}
                  <strong>{c.title}</strong>
                </div>
                <div class="muted small">{c.snippet ? <Snippet text={c.snippet} /> : c.summary}</div>
              </a>
            </li>
          ))}
          {q && (
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                  navigate(`/search?q=${encodeURIComponent(q)}`);
                }}
              >
                <span class="muted">See all results for “{q}” →</span>
              </a>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
